<template>
  <div id="JtInfoCard">
    <div
      v-if="iconName"
      class="card-icon-div"
      :style="{ borderColor: accentColor }"
    >
      <q-icon size="sm" :name="iconName" :style="{ color: accentColor }" />
    </div>
    <div class="card-info-div">
      <q-icon
        v-if="isLoading"
        class="rotate"
        size="sm"
        name="autorenew"
        :style="{ color: accentColor }"
      >
        <q-tooltip
          anchor="bottom middle"
          self="top middle"
          content-style="font-size: 13px"
          :offset="[5, 5]"
        >
          {{ $gettext('LOADING') }}
        </q-tooltip>
      </q-icon>
      <span
        v-if="!isLoading"
        class="card-info-value"
        :style="{ color: accentColor }"
        @mouseover="onActivateTooltipValue"
      >
        <q-tooltip
          v-if="isShowTooltipValue"
          anchor="bottom middle"
          self="top middle"
          content-style="font-size: 13px"
          :offset="[5, 5]"
        >
          {{ computedTextValue }}
        </q-tooltip>
        {{ computedTextValue }}
      </span>
      <span class="card-info-label" @mouseover="onActivateTooltipLabel">
        <q-tooltip
          v-if="isShowTooltipLabel"
          anchor="bottom middle"
          self="top middle"
          content-style="font-size: 13px"
          :offset="[5, 5]"
        >
          {{ computedLabel }}
        </q-tooltip>
        {{ computedLabel }}
      </span>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { repository } from 'src/jtech/jtech-repository'
import { Component, Vue } from 'vue-property-decorator'
import { CURRENCY_BY_COUNTRY, dashboardMethods, TEXT_FORMATTING_TYPE } from '../shared'

@Component({
  name: 'JtInfoCard',
  props: {
    accentColor: {
      type: String,
      required: false,
      default: 'black'
    },
    cardKey: {
      type: [Number, String],
      required: true
    },
    decimalPlaces: {
      type: Number,
      required: false
    },
    nmGrid: {
      type: String,
      required: false
    },
    iconName: {
      type: String,
      required: false
    },
    infoType: {
      type: String,
      require: false,
      default: TEXT_FORMATTING_TYPE.DEFAULT
    },
    nmInterface: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: true
    },
    nmGridProperty: {
      type: String,
      required: false
    },
    shouldFormatValue: {
      type: Boolean,
      required: true
    },
    shouldTranslateLabel: {
      type: String,
      required: false
    },
    value: {
      type: [String, Number],
      required: false
    },
    parameters: {
      type: String,
      required: false,
      default: ''
    }
  }
})
export default class JtInfoCard extends Vue {
  isShowTooltipLabel = false
  isShowTooltipValue = false
  baseValue = '';
  isLoading = false

  async mounted () {
    await this.reload()
  }

  get computedLabel () {
    return this.shouldTranslateLabel ? this.$gettext(this.label) : this.label
  }

  get computedTextValue () {
    return this.formatValue(this.baseValue)
  }

  onActivateTooltipLabel (evt) {
    const targetEl = evt.target
    if (targetEl.offsetWidth < targetEl.scrollWidth) {
      this.isShowTooltipLabel = true
    } else {
      this.isShowTooltipLabel = false
    }
  }

  onActivateTooltipValue (evt) {
    const targetEl = evt.target
    if (targetEl.offsetWidth < targetEl.scrollWidth) {
      this.isShowTooltipValue = true
    } else {
      this.isShowTooltipValue = false
    }
  }

  formatDateValue (baseValue) {
    if (this.isValidFormatParamDate(baseValue)) {
      const momentValue = moment(baseValue, 'YYYY-MM-DD')
      momentValue.locale(this.$language.current)
      return momentValue.format('DD/MMM./YYYY')
    } else {
      return baseValue
    }
  }

  formatValue (baseValue) {
    if (this.shouldFormatValue) {
      switch (this.infoType) {
        case TEXT_FORMATTING_TYPE.DATE:
          return this.formatDateValue(baseValue)
        case TEXT_FORMATTING_TYPE.DEFAULT:
        case TEXT_FORMATTING_TYPE.PERCENT:
        case TEXT_FORMATTING_TYPE.CURRENCY:
        case TEXT_FORMATTING_TYPE.KG:
        case TEXT_FORMATTING_TYPE.TON:
        case TEXT_FORMATTING_TYPE.M2:
        case TEXT_FORMATTING_TYPE.M3:
        case TEXT_FORMATTING_TYPE.LITRO:
          return dashboardMethods.getTextFormat(baseValue, this.decimalPlaces, this.infoType, this.$language.current)
        default:
          return baseValue
      }
    }
    return baseValue
  }

  async getInfoFromServer () {
    this.isLoading = true
    if (this.nmInterface && this.nmGrid && this.nmGridProperty) {
      try {
        const { registros: records } = await repository.resolveDatatableModel(this.nmInterface, { grid: this.nmGrid, ...this.parameters })
        if (records.length == 0) {
          console.error(`JtInfoCard: ${this.cardKey}. No records returned.`)
        } else if (records.length == 1) {
          this.isLoading = false
          return records[0][this.nmGridProperty] || this.$gettext('NO_DATA')
        } else if (records.length > 0) {
          console.error(`JtInfoCard: ${this.cardKey}. Too many rows returned.`)
        }
      } catch {
        console.error(`JtInfoCard: ${this.cardKey}. Request error.`)
      }
    } else {
      console.error(`JtInfoCard: ${this.cardKey}. Missing "nmInterface" or "nmGrid" or "nmGridProperty".`)
    }
    this.isLoading = false
    return this.$gettext('NO_DATA')
  }

  isValidFormatParamDate (baseValue) {
    const regexExp = /(\d{4})-(\d{2})-(\d{2})/
    return regexExp.test(baseValue)
  }

  async reload () {
    this.baseValue = this.value || this.value === 0 ? this.value : await this.getInfoFromServer()
  }
}
</script>

<style lang='stylus' scoped>
@import '~quasar-variables';

#JtInfoCard {
  height: 100%;
  display: flex;
  align-items: center;

  .card-icon-div {
    border: solid 1px;
    border-radius: 50%;
    width: fit-content;
    padding: 5px;
    margin-right: 20px;
    width: 36px;
    height: 36px;
  }

  .card-info-div {
    display: flex;
    flex-direction: column;
    width: calc(100% - 57px);

    .card-info-value {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      font-size: large;
    }

    .card-info-label {
      height: auto;
      color: #394053;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      font-size: small;
    }
  }
}
</style>
